import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { BrandingField, defaultBrandingSettings } from '@app/core/models/branding.models';
import { AppFeature } from '@app/core/models/config.models';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppTranslateService } from '@app/core/services/app-translate.service';
import { BrandingService } from '@app/core/services/branding.service';
import { SnackbarService } from '@app/core/services/snack-bar.service';
import { ElectronService } from '@app/electron/electron.service';
import { CompanionRingingMode, DefaultCompanionSettings } from '@app/phone/models/companion.models';
import { CompanionService } from '@app/phone/services/companion.service';
import { SettingsPage } from '@app/preferences/models/settings-page';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom, merge } from 'rxjs';

@UntilDestroy()
@Component({
  standalone: true,
  imports: [
    CommonModule,
    MatCheckbox,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltip,
    ReactiveFormsModule,
    TranslateModule,
  ],
  selector: 'app-calling',
  templateUrl: './calling.component.html',
  styleUrls: ['./calling.component.scss'],
})
export class CallingComponent extends SettingsPage {
  protected companionForm: FormGroup;
  protected alwaysOnTopControl = new FormControl(true, { nonNullable: true });
  protected autoDialTelLinksControl = new FormControl(false, { nonNullable: true });
  protected surfaceWhenCallRingsInControl = new FormControl(false, { nonNullable: true });

  protected readonly AppFeature = AppFeature;
  protected readonly CompanionRingingMode = CompanionRingingMode;

  protected readonly BrandingField = BrandingField;
  protected defaultBrandingSettings = defaultBrandingSettings;

  constructor(
    private fb: FormBuilder,
    private companionService: CompanionService,
    protected brandingService: BrandingService,
    protected appConfigService: AppConfigService,
    protected electronService: ElectronService,
    private snackbarService: SnackbarService,
    private translationService: AppTranslateService
  ) {
    super();

    this.companionForm = this.fb.group({
      rememberState: DefaultCompanionSettings.remember,
      ringingMode: DefaultCompanionSettings.ringing,
    });

    this.alwaysOnTopControl.patchValue(appConfigService.callingSettings?.activeCallWindowAlwaysOnTop ?? false);
    this.autoDialTelLinksControl.patchValue(appConfigService.callingSettings?.autoDialTelLinks ?? false);
    this.surfaceWhenCallRingsInControl.patchValue(appConfigService.callingSettings?.surfaceWhenCallRingsIn ?? false);

    this.companionService.companionSettings$.pipe(untilDestroyed(this)).subscribe((settings) => {
      this.companionForm.patchValue({ rememberState: settings.remember, ringingMode: settings.ringing });
    });

    merge(
      this.companionForm.statusChanges,
      this.alwaysOnTopControl.statusChanges,
      this.autoDialTelLinksControl.statusChanges,
      this.surfaceWhenCallRingsInControl.statusChanges
    )
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.disabled =
          this.companionForm.pristine &&
          this.alwaysOnTopControl.pristine &&
          this.autoDialTelLinksControl.pristine &&
          this.surfaceWhenCallRingsInControl.pristine;
      });
  }

  public async save() {
    this.loading = true;
    try {
      if (this.companionForm.dirty) {
        const preferences = this.companionForm.value;

        await firstValueFrom(this.companionService.storePreferences(preferences));
      }

      if (
        this.alwaysOnTopControl.dirty ||
        this.autoDialTelLinksControl.dirty ||
        this.surfaceWhenCallRingsInControl.dirty
      ) {
        await firstValueFrom(
          this.appConfigService.updatePreferences({
            calling: {
              ...this.appConfigService.callingSettings,
              activeCallWindowAlwaysOnTop: this.alwaysOnTopControl.value,
              autoDialTelLinks: this.autoDialTelLinksControl.value,
              surfaceWhenCallRingsIn: this.surfaceWhenCallRingsInControl.value,
            },
          })
        );
      }
    } catch (error) {
      console.error('Error while saving Calling preferences:', error);
      this.snackbarService.open(this.translationService.instant('CALLING_PREFERENCES.SAVING_FAILED'));
    }

    this.loading = false;
  }
}
